import { ArrowLeft, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button.js';
import { AIReasoningStepper, type AIStep } from '@/components/ui/ai-stepper.js';
import { MarkdownContent } from '@/components/ui/markdown-content.js';
import { Link } from 'wouter';

interface EvidenceChip {
  label: string;
  href: string;
}

interface AnsweredStateProps {
  question: string;
  answer: string | null;
  isStreaming: boolean;
  steps: AIStep[];
  evidenceChips?: EvidenceChip[];
  onBack: () => void;
}

export function AnsweredState({
  question,
  answer,
  isStreaming,
  steps,
  evidenceChips = [],
  onBack,
}: AnsweredStateProps) {
  return (
    <div className="flex-1 flex flex-col max-w-[720px] mx-auto px-6 py-8 w-full">
      <Button variant="ghost" size="sm" onClick={onBack} className="self-start mb-6 gap-2">
        <ArrowLeft className="h-3.5 w-3.5" />
        Back
      </Button>

      <p className="text-xs text-muted-foreground mb-2">You asked</p>
      <h1 className="text-lg font-semibold text-foreground mb-6 leading-snug">{question}</h1>

      {isStreaming && !answer && <AIReasoningStepper steps={steps} className="mb-6" />}

      {answer && (
        // ERA-014: render the assistant's markdown (headings, tables, bold) instead of dumping the
        // raw source string. Reuses the same MarkdownContent renderer as the Help surfaces.
        <MarkdownContent className="mb-6">{answer}</MarkdownContent>
      )}

      {evidenceChips.length > 0 && (
        <div className="border-t border-border pt-4">
          <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
            Evidence cited
          </p>
          <div className="flex flex-wrap gap-2">
            {evidenceChips.map(chip => (
              <Link key={chip.href} href={chip.href}>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[9px] border border-border bg-card text-xs text-foreground hover:bg-[oklch(0.97_0.004_250)] transition-colors cursor-pointer">
                  {chip.label}
                  <ExternalLink className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
